import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faChevronRight, faEnvelope, faCheck } from "@fortawesome/free-solid-svg-icons";
import logo from '../assets/logo.png';
import { supabase } from '../supabaseClient';

const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBgLight: "rgba(12,105,200,0.12)",
  accentBorder: "rgba(12,105,200,0.20)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgAlt: "#111111",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)",
  shadowLg: "0 12px 40px rgba(0,0,0,0.5)",
  text: "#FAFAFA",
  textSecondary: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)",
  scrollbarThumb: "rgba(255,255,255,0.12)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.35)",
  inputBg: "rgba(255,255,255,0.04)",
  inputBorder: "rgba(255,255,255,0.10)",
  inputText: "#FFFFFF",
  inputPlaceholder: "rgba(255,255,255,0.35)",
  success: "#34D399",
  danger: "#EF4444",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.15)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgAlt: "#EDF0F5",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  shadowLg: "0 12px 40px rgba(10,29,68,0.10)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)",
  scrollbarThumb: "rgba(10,29,68,0.12)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow: "rgba(12,105,200,0.25)",
  inputBg: "#F8F9FC",
  inputBorder: "rgba(10,29,68,0.12)",
  inputText: "#0F1629",
  inputPlaceholder: "rgba(10,29,68,0.35)",
  success: "#059669",
  danger: "#DC2626",
};

const faqs = [
  { q: "How do I book a sports location?", a: "Go to the Home screen, select \"Book a Location,\" and choose a venue and time slot. Pay via ProAm Coins or card." },
  { q: "How do I join or create a community?", a: "Navigate to the Chat screen, select the Community Section, and either browse existing communities or create a new one with the \"Add\" button." },
  { q: "How do I invite friends to join my game or team?", a: "Go to the Sports section in the Home screen, tap on your team, and select \"Invite\" to send an invitation to your friends." },
  { q: "How can I manage my team and team members?", a: "From the Sports section, select your team and access the team profile. Here, you can add or remove members and modify team settings." },
  { q: "How do I stake ProAm Coins in a match?", a: "Ensure your account is verified in the profile settings. Once verified, use the \"Stake Coins\" feature before a match to set the stakes." },
  { q: "How does the leaderboard ranking work?", a: "The leaderboard is based on your trophies, which you earn by winning matches. You can filter rankings globally, nationally, or regionally." },
  { q: "What happens if I lose connection during a match?", a: "If you lose connection mid-match, the system will attempt to reconnect you. If not, the match will be considered forfeited and you may lose trophies." },
  { q: "How can I reset my password?", a: "Go to the Profile screen, tap on \"Settings,\" and select \"Change Password\" to reset your password securely." },
  { q: "How do I suggest a new sports venue?", a: "On the Map screen, use the \"Flag\" button to suggest a new venue. Enter the location details and upload an image if available." },
  { q: "Can I play sports in different locations around the world?", a: "Yes, the app allows you to explore sports locations globally. You can adjust the search radius or filter locations to find places near your travel destinations." },
  { q: "What are ProAm Coins used for?", a: "ProAm Coins can be used for booking venues, staking in matches, enrolling in tournaments, or other in-app purchases." },
  { q: "How do I create a private training session?", a: "Use the \"Add Session\" feature in the Schedule section, select \"Private,\" and invite participants directly. The location will be hidden until they're accepted." },
  { q: "How do I view past bookings?", a: "Tap the filter icon on the Bookings screen to view all your past bookings. You can access details such as venue name, date, and time." },
  { q: "How do I check weather conditions before a game?", a: "The weather icon on the Matchmaking screen provides real-time weather updates for your current or selected location." },
  { q: "What happens when I forfeit a match?", a: "If you forfeit, you automatically lose, and trophies will be deducted from your total based on the match outcome." },
  { q: "How do I upload and share highlight videos?", a: "Go to the Highlights screen, tap the \"Add\" icon, upload your video, add a caption, and it will be shared with the community." },
  { q: "Can I change the sport I'm currently focused on?", a: "Yes, you can switch between different sports by going to the Home screen and selecting your preferred sport from the Sports tab." },
  { q: "How do I track my steps and trophies?", a: "The Fitness section automatically tracks your steps and converts them into trophies, contributing to your leaderboard ranking." },
  { q: "How do I enrol in tournaments?", a: "Open the Tournaments section in the Home screen, select the tournament you want to join, and enrol by using ProAm Coins." },
  { q: "How can I report an issue with another player?", a: "Use the \"Flag\" button during a match or in the player's profile to report any rule violations or inappropriate behaviour." },
];

const Support = () => {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setIsDark((v) => {
      const next = !v;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);

  const [openFaq, setOpenFaq] = useState(-1);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    try {
      setErrorMessage('');
      const { error } = await supabase.from('feedback').insert({
        message: feedback.trim(),
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      if (error) throw error;
      setSubmitted(true);
      setFeedback('');
    } catch (err) {
      setErrorMessage('There was an issue submitting your feedback. Please try again later.');
    }
  };

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'DM Sans', sans-serif",
      transition: "background 0.3s ease, color 0.3s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        a { color: inherit; text-decoration: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
        .theme-toggle { transition: transform 0.2s ease; }
        .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
        .card-hover { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .card-hover:hover { border-color: ${C.accentBorder} !important; box-shadow: ${C.shadowMd} !important; }
        .grad-text {
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .faq-answer {
          display: grid; grid-template-rows: 0fr;
          transition: grid-template-rows 0.35s cubic-bezier(0.16,1,0.3,1);
        }
        .faq-answer.open { grid-template-rows: 1fr; }
        .faq-answer > div { overflow: hidden; }
        textarea:focus { outline: none; border-color: rgba(12,105,200,0.5) !important; box-shadow: 0 0 0 3px rgba(12,105,200,0.10) !important; }
        .submit-btn { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .submit-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 28px ${C.accentGlow}; }
        .submit-btn:active { transform: translateY(1px); }
      `}</style>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        height: 64, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.3s ease",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logo} alt="Snows ProAm" style={{ height: 30 }} />
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/" style={{
            fontSize: 13.5, fontWeight: 500, color: C.textSecondary,
            padding: "6px 14px", borderRadius: 8,
            border: `1px solid ${C.border}`, background: C.bgElevated,
          }}>← Back</a>
          <button className="theme-toggle" aria-label={isDark ? "Use light theme" : "Use dark theme"} onClick={toggleTheme}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: C.bgElevated, border: `1px solid ${C.border}`,
              color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <div style={{ width: "100%", maxWidth: 800, margin: "0 auto", padding: "64px clamp(16px, 5vw, 24px) 48px", boxSizing: "border-box" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
          fontSize: 12, fontWeight: 600, color: C.accent,
          textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 20,
        }}>Help Centre</div>

        <h1 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(36px, 6vw, 56px)",
          fontWeight: 800, letterSpacing: -1.2, lineHeight: 1.05,
          marginBottom: 16, color: C.text,
        }}>
          How can we <span className="grad-text">help you?</span>
        </h1>

        <p style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6, maxWidth: 520 }}>
          Whether you're scheduling a game, joining a community, or competing in matches — we're here to help.
        </p>
      </div>

      <div style={{ width: "100%", maxWidth: 800, margin: "0 auto", padding: "0 clamp(16px, 5vw, 24px) 100px", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 32, boxSizing: "border-box", overflow: "hidden" }}>

        {/* FAQ */}
        <div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>FAQ</div>
            <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 24, fontWeight: 800, letterSpacing: -0.4, color: C.text }}>
              Frequently Asked Questions
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            {faqs.map((f, i) => (
              <div key={f.q} style={{
                borderRadius: 14, border: `1px solid ${C.border}`,
                background: openFaq === i ? C.bgCard : "transparent",
                boxShadow: openFaq === i ? C.shadowMd : "none",
                transition: "background 0.2s ease, box-shadow 0.2s ease",
              }}>
                <button aria-expanded={openFaq === i} aria-controls={`support-answer-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  style={{
                    width: "100%", minWidth: 0, padding: "16px clamp(14px, 4vw, 20px)",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16,
                    background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: C.text,
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: C.accent,
                      minWidth: 24, fontFamily: "'Sora', sans-serif",
                    }}>{String(i + 1).padStart(2, "0")}</span>
                    <span style={{ minWidth: 0, fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", overflowWrap: "anywhere" }}>{f.q}</span>
                  </div>
                  <FontAwesomeIcon icon={faChevronRight} style={{
                    fontSize: 11, color: C.textMuted, flexShrink: 0,
                    transform: openFaq === i ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform 0.25s ease",
                  }} />
                </button>
                <div id={`support-answer-${i}`} aria-hidden={openFaq !== i} className={`faq-answer ${openFaq === i ? "open" : ""}`}>
                  <div>
                    <div style={{ padding: "0 20px 16px 56px", fontSize: 14, color: C.textSecondary, lineHeight: 1.65 }}>{f.a}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feedback form */}
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`,
          borderRadius: 20, padding: "32px 28px", boxShadow: C.shadowMd,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Feedback</div>
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, letterSpacing: -0.4, color: C.text, marginBottom: 6 }}>
            Submit Your Feedback
          </h2>
          <p style={{ fontSize: 14, color: C.textSecondary, marginBottom: 20, lineHeight: 1.6 }}>
            Describe your issue or share ideas — we read everything.
          </p>

          {submitted ? (
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "16px 20px", borderRadius: 12,
              background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: C.accentBgLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: 13, color: C.accent }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Feedback received!</div>
                <div style={{ fontSize: 13, color: C.textSecondary }}>Thank you — we appreciate your input.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12 }}>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Please describe your issue or provide feedback..."
                rows={5}
                style={{
                  width: "100%", padding: "14px 16px", borderRadius: 12,
                  border: `1px solid ${C.inputBorder}`, background: C.inputBg,
                  color: C.inputText, fontSize: 14, fontFamily: "'DM Sans', sans-serif",
                  resize: "vertical", transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                }}
              />
              {errorMessage && (
                <div style={{
                  padding: "10px 14px", borderRadius: 10,
                  background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.18)",
                  color: C.danger, fontSize: 13, fontWeight: 600,
                }}>{errorMessage}</div>
              )}
              <button type="submit" className="submit-btn"
                style={{
                  background: C.accentGrad, border: "none", color: "#fff",
                  padding: "13px 28px", borderRadius: 10, cursor: "pointer",
                  fontSize: 14, fontWeight: 700, fontFamily: "'Sora', sans-serif",
                  alignSelf: "flex-start",
                }}>
                Submit Feedback
              </button>
            </form>
          )}
        </div>

        {/* Contact */}
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`,
          borderRadius: 20, padding: "32px 28px", boxShadow: C.shadowMd,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Contact</div>
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, letterSpacing: -0.4, color: C.text, marginBottom: 20 }}>
            Additional Help
          </h2>

          <div style={{ display: "grid", gap: 12 }}>
            {[
              { label: "General Support", email: "support@snowsproam.com", desc: "App issues, account help, general queries." },
              { label: "Institutions", email: "institutions@snowsproam.com", desc: "Schools, clubs, companies, and organisations." },
              { label: "Direct Contact", email: "mail@snowsproam.com", desc: "Partnership and direct enquiries." },
            ].map((c) => (
              <a key={c.email} href={`mailto:${c.email}`} className="card-hover"
                style={{
                  display: "flex", alignItems: "center", gap: 14, padding: "16px 18px",
                  borderRadius: 14, background: C.bgAlt, border: `1px solid ${C.border}`,
                  textDecoration: "none",
                }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 10,
                  background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 14, color: C.accent }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{c.label}</div>
                  <div style={{ fontSize: 12.5, color: C.accent, fontWeight: 600, margin: "2px 0" }}>{c.email}</div>
                  <div style={{ fontSize: 12, color: C.textMuted }}>{c.desc}</div>
                </div>
              </a>
            ))}
          </div>

          <p style={{ marginTop: 20, fontSize: 14, color: C.textSecondary, lineHeight: 1.6 }}>
            You can also explore our detailed guides within the app, which cover features like booking, matchmaking, team management, and community interaction.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Support;
